/**
 * Shared user-settings hook for Meer + Toegankelijkheid (CONTRACTS.md §3).
 *
 * Behaviour contract:
 * - Loads via repositories.profile.getSettings() (server is source of truth).
 * - Updates are optimistic with rollback: a failed write restores the previous
 *   value and reports 'sync_failed' via SyncStatus — never a fake save.
 * - Offline (navigator.onLine === false): the change is kept, labelled
 *   'sync_queued' ('Wacht op synchronisatie') and enqueued in the bounded sync
 *   queue ('rust-sync-queue'); SyncProvider retries it on reconnect.
 * - SUPABASE_NOT_CONFIGURED: settings persist as an explicit device-only copy
 *   ('rust-settings-device') labelled 'Op apparaat opgeslagen' — an honest
 *   local-draft state, not a hidden fallback.
 * - Display preferences (reducedMotion / highContrast / textSize) are applied
 *   to <html> data-attributes on load and on every change via
 *   applyDisplayPreferences().
 * - navCollapsed is mirrored to the 'rust-nav-collapsed' UI cache so BottomNav
 *   picks it up on next mount (contract §1).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { repositories } from '@/services/repositories'
import { enqueueWrite, registerSyncHandler } from '@/services/sync'
import { isCloudConfigured } from '@/lib/supabase/client'
import { isAppError, toAppError } from '@/lib/supabase/errors'
import { NAV_COLLAPSED_STORAGE_KEY } from '@/components/navigation/use-nav-collapse'
import type { AppError, SyncSaveState, UserSettings } from '@/types'
import { applyDisplayPreferences } from './applyPreferences'
import './settings.css'

export type SettingsValues = Omit<UserSettings, 'userId' | 'updatedAt'>

export const DEFAULT_SETTINGS: SettingsValues = {
  haptics: true,
  sounds: false,
  breathingVibration: true,
  plannerHaptics: true,
  reducedMotion: false,
  highContrast: false,
  textSize: 'normal',
  navCollapsed: false,
  darkMode: false,
}

/** Device-only settings cache (explicit offline/local-draft copy). */
const DEVICE_SETTINGS_KEY = 'rust-settings-device'

/** Sync-queue write kind for offline settings upserts. */
const SETTINGS_WRITE_KIND = 'profile-settings-upsert'

registerSyncHandler(SETTINGS_WRITE_KIND, async (payload) => {
  await repositories.profile.upsertSettings(payload as Partial<SettingsValues>)
})

function sanitizeTextSize(value: unknown): SettingsValues['textSize'] {
  return value === 'small' || value === 'large' ? value : 'normal'
}

function readDeviceSettings(): SettingsValues | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(DEVICE_SETTINGS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SettingsValues>
    return {
      haptics: typeof parsed.haptics === 'boolean' ? parsed.haptics : DEFAULT_SETTINGS.haptics,
      sounds: typeof parsed.sounds === 'boolean' ? parsed.sounds : DEFAULT_SETTINGS.sounds,
      breathingVibration:
        typeof parsed.breathingVibration === 'boolean'
          ? parsed.breathingVibration
          : DEFAULT_SETTINGS.breathingVibration,
      plannerHaptics:
        typeof parsed.plannerHaptics === 'boolean' ? parsed.plannerHaptics : DEFAULT_SETTINGS.plannerHaptics,
      reducedMotion:
        typeof parsed.reducedMotion === 'boolean' ? parsed.reducedMotion : DEFAULT_SETTINGS.reducedMotion,
      highContrast:
        typeof parsed.highContrast === 'boolean' ? parsed.highContrast : DEFAULT_SETTINGS.highContrast,
      textSize: sanitizeTextSize(parsed.textSize),
      navCollapsed:
        typeof parsed.navCollapsed === 'boolean' ? parsed.navCollapsed : DEFAULT_SETTINGS.navCollapsed,
      darkMode: typeof parsed.darkMode === 'boolean' ? parsed.darkMode : DEFAULT_SETTINGS.darkMode,
    }
  } catch {
    return null
  }
}

function writeDeviceSettings(values: SettingsValues): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(DEVICE_SETTINGS_KEY, JSON.stringify(values))
  } catch {
    /* Cache write failing is non-fatal: the in-memory state stays correct. */
  }
}

function mirrorNavCollapsed(navCollapsed: boolean): void {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, String(navCollapsed))
  } catch {
    /* localStorage unavailable — server settings stay authoritative */
  }
}

export type UserSettingsState = {
  values: SettingsValues
  loading: boolean
  /** False when the cloud is not configured or settings could not be read. */
  cloudAvailable: boolean
  syncState: SyncSaveState | null
  error: AppError | null
  /** Optimistic update with rollback / offline queueing. */
  update: (patch: Partial<SettingsValues>) => void
  /** Re-runs the initial load (e.g. after a load error). */
  retry: () => void
}

export function useUserSettings(): UserSettingsState {
  const [values, setValues] = useState<SettingsValues>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [cloudAvailable, setCloudAvailable] = useState<boolean>(isCloudConfigured)
  const [syncState, setSyncState] = useState<SyncSaveState | null>(null)
  const [error, setError] = useState<AppError | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  // Serializes writes so a slow write cannot rollback over a newer change.
  const valuesRef = useRef(values)
  valuesRef.current = values

  useEffect(() => {
    let cancelled = false
    if (!isCloudConfigured()) {
      const device = readDeviceSettings() ?? DEFAULT_SETTINGS
      setValues(device)
      setCloudAvailable(false)
      setLoading(false)
      applyDisplayPreferences(device)
      return undefined
    }
    repositories.profile
      .getSettings()
      .then((settings) => {
        if (cancelled) return
        const next: SettingsValues = settings
          ? {
              haptics: settings.haptics,
              sounds: settings.sounds,
              breathingVibration: settings.breathingVibration,
              plannerHaptics: settings.plannerHaptics,
              reducedMotion: settings.reducedMotion,
              highContrast: settings.highContrast,
              textSize: sanitizeTextSize(settings.textSize),
              navCollapsed: settings.navCollapsed,
              darkMode: settings.darkMode,
            }
          : DEFAULT_SETTINGS
        setValues(next)
        setCloudAvailable(true)
        setError(null)
        applyDisplayPreferences(next)
        writeDeviceSettings(next)
      })
      .catch((loadError: unknown) => {
        if (cancelled) return
        const fallback = readDeviceSettings() ?? DEFAULT_SETTINGS
        setValues(fallback)
        setCloudAvailable(false)
        setError(
          isAppError(loadError) && loadError.code === 'SUPABASE_NOT_CONFIGURED'
            ? null
            : toAppError(loadError, 'database'),
        )
        applyDisplayPreferences(fallback)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  const update = useCallback((patch: Partial<SettingsValues>) => {
    const previous = valuesRef.current
    const next: SettingsValues = { ...previous, ...patch }
    setValues(next)
    setError(null)
    applyDisplayPreferences(next)
    writeDeviceSettings(next)
    if (patch.navCollapsed !== undefined) mirrorNavCollapsed(patch.navCollapsed)

    if (!isCloudConfigured()) {
      // Honest device-only state: kept locally, labelled as such.
      setCloudAvailable(false)
      setSyncState('local_saved')
      return
    }

    const commit = async (): Promise<void> => {
      setSyncState('syncing')
      try {
        await repositories.profile.upsertSettings(patch)
        setSyncState('cloud_saved')
      } catch (writeError: unknown) {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false
        const appError = toAppError(writeError, 'database')
        if (offline || appError.code === 'NETWORK_UNREACHABLE') {
          // Keep the change and queue the latest full snapshot; the queue
          // retries on reconnect. dedupeKey collapses repeated toggles.
          try {
            enqueueWrite({
              kind: SETTINGS_WRITE_KIND,
              payload: next,
              dedupeKey: 'profile-settings',
            })
            setSyncState('sync_queued')
          } catch {
            // Queue is full: rollback honestly instead of pretending.
            const current = valuesRef.current
            const rolledBack: SettingsValues = { ...current, ...pickPrevious(previous, patch) }
            setValues(rolledBack)
            applyDisplayPreferences(rolledBack)
            writeDeviceSettings(rolledBack)
            setSyncState('sync_failed')
            setError(appError)
          }
          return
        }
        // Non-offline failure: rollback only the keys this patch touched so
        // a newer change made while the write was in flight is preserved.
        const current = valuesRef.current
        const rolledBack: SettingsValues = { ...current, ...pickPrevious(previous, patch) }
        setValues(rolledBack)
        applyDisplayPreferences(rolledBack)
        writeDeviceSettings(rolledBack)
        if (patch.navCollapsed !== undefined) mirrorNavCollapsed(rolledBack.navCollapsed)
        setSyncState('sync_failed')
        setError(appError)
      }
    }
    void commit()
  }, [])

  const retry = useCallback(() => {
    setLoading(true)
    setError(null)
    setLoadAttempt((attempt) => attempt + 1)
  }, [])

  return { values, loading, cloudAvailable, syncState, error, update, retry }
}

/** Extracts the previous values for only the keys present in `patch`. */
function pickPrevious(previous: SettingsValues, patch: Partial<SettingsValues>): Partial<SettingsValues> {
  const result: Partial<SettingsValues> = {}
  for (const key of Object.keys(patch) as (keyof SettingsValues)[]) {
    ;(result as Record<keyof SettingsValues, unknown>)[key] = previous[key]
  }
  return result
}
